import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();

    // For WorkOS authentication, include credentials (cookies) for all API requests
    if (req.url.includes('/api/')) {
        newReq = req.clone({
            withCredentials: true,
        });
    }

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    //
    // IMPORTANT: Do NOT add Authorization header for S3 presigned URLs
    // S3 presigned URLs already contain authentication in the URL itself,
    // and adding an Authorization header will cause a 400 Bad Request error.
    const isS3Request = req.url.includes('s3.amazonaws.com') || req.url.includes('.s3.');
    
    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken) &&
        !isS3Request
    ) {
        newReq = req.clone({
            headers: req.headers.set(
                'Authorization',
                'Bearer ' + authService.accessToken
            ),
            withCredentials: newReq.withCredentials,
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // For WorkOS, redirect to marketing landing (root)
                window.location.href = '/';
            }

            return throwError(error);
        })
    );
};
